/* Sequoia Fitness | Website: https://www.sequoia.fitness | Author: Ali Hosseini | Copyright (c) 2026 sequoia.fitness. All rights reserved. */
/* Designed and developed by Pouria Shabani | https://linkedin.com/in/pouriashabani */

import { useMemo, useRef, useState } from 'react';
import { NumField, ToolHead, normalizeDigits } from './ToolUi.jsx';
import BmiResultDock from './BmiResultDock.jsx';
import './bmi-brutal.css';

const ACTIVITY_LEVELS = [
{ value: 1.2, label: 'ساکن' },
{ value: 1.375, label: 'کم‌تحرک' },
{ value: 1.55, label: 'متوسط' },
{ value: 1.725, label: 'فعال' },
{ value: 1.9, label: 'زیاد' }];
const GENDERS = [
{ id: 'male', label: 'مرد' },
{ id: 'female', label: 'زن' }];
const BMI_BUCKETS = [
{ max: 18.5, label: 'کمبود وزن', fill: 'bg-sky-300', color: '#7dd3fc' },
{ max: 25, label: 'نرمال', fill: 'bg-emerald-300', color: '#6ee7b7' },
{ max: Infinity, label: 'اضافه وزن', fill: 'bg-rose-400', color: '#fb7185' }];
function bmiBucket(bmi) {
  return BMI_BUCKETS.find((b) => bmi < b.max) || BMI_BUCKETS[BMI_BUCKETS.length - 1];
}
const BMI_SCALE = [
{ to: 18.5, w: 3.5, color: '#7dd3fc' },
{ to: 25, w: 6.5, color: '#6ee7b7' },
{ to: 30, w: 5, color: '#fb7185' },
{ to: 35, w: 5, color: '#f43f5e' },
{ to: 40, w: 5, color: '#e11d48' },
{ to: 45, w: 5, color: '#be123c' }];
const SCALE_TOTAL = BMI_SCALE.reduce((sum, z) => sum + z.w, 0);
const SCALE_TICKS = BMI_SCALE.slice(0, -1).map((z, i) => ({
  label: z.to,
  at: BMI_SCALE.slice(0, i + 1).reduce((sum, q) => sum + q.w, 0) / SCALE_TOTAL * 100
}));
function bmiTone(bmi) {
  const zone = BMI_SCALE.find((z) => bmi < z.to);
  return (zone || BMI_SCALE[BMI_SCALE.length - 1]).color;
}
const scalePos = (bmi) => Math.max(2, Math.min(98, (bmi - 15) / (45 - 15) * 100));

export default function BmiCalculator() {
  const [gender, setGender] = useState('male');
  const [age, setAge] = useState('');
  const [activity, setActivity] = useState(1.55);
  const [height, setHeight] = useState(170);
  const [weight, setWeight] = useState(70);
  const [report, setReport] = useState(null);
  const [validationMessage, setValidationMessage] = useState('');
  const resultRef = useRef(null);
  const bmi = weight / (height / 100) ** 2;
  const bucket = bmiBucket(bmi);
  const activityLabel = useMemo(
    () => ACTIVITY_LEVELS.find((level) => level.value === activity)?.label || '',
    [activity]
  );
  function handleCalculate() {
    const ageNum = parseInt(normalizeDigits(age), 10);
    if (!ageNum || !weight || !height) {
      setValidationMessage('لطفاً سن رو وارد کن تا تحلیل انجام بشه.');
      return;
    }
    setValidationMessage('');
    const bmr = 10 * weight + 6.25 * height - 5 * ageNum + (gender === 'male' ? 5 : -161);
    const tdee = bmr * activity;
    setReport({
      bmi,
      bmiCat: bucket.label,
      bucket,
      height,
      weight,
      activityLabel,
      bmr: Math.round(bmr),
      tdee: Math.round(tdee),
      maintain: Math.round(tdee),
      lossLow: Math.round(tdee * 0.8),
      lossHigh: Math.round(tdee * 0.85),
      gainLow: Math.round(tdee * 1.1),
      gainHigh: Math.round(tdee * 1.15)
    });
    setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
  }
  return (
    <div className="seq-bmi-brutal-root">
      <div className="tl-page">
        <ToolHead title="محاسبه‌گر BMI" icon="/a/tool-icon-bmi.webp" badge="BMI" caption="شاخص توده بدنی، سوخت‌وساز پایه و کالری روزانه بر پایه فرمول Mifflin-St Jeor" />
        <div className="tl-grid">
          <section className="tl-col">
            <div className="tl-card tl-card--yellow">
              <div className="tl-field__top"><span className="tl-field__label">جنسیت</span></div>
              <div className="tl-seg" role="group" aria-label="جنسیت">
                {GENDERS.map(({ id, label }) => (
                  <button key={id} type="button" aria-pressed={gender === id} className={gender === id ? 'is-on' : ''} onClick={() => setGender(id)}>
                    {label}
                  </button>
                ))}
              </div>
              <div className="tl-selects">
                <label>
                  <span>سن</span>
                  <input
                    className="tl-select"
                    type="text"
                    inputMode="numeric"
                    value={age}
                    placeholder="۲۸"
                    aria-label="سن"
                    onChange={(e) => {
                      setAge(e.target.value);
                      if (validationMessage) setValidationMessage('');
                    }}
                  />
                </label>
                <label>
                  <span>سطح فعالیت</span>
                  <select className="tl-select" value={activity} onChange={(e) => setActivity(parseFloat(e.target.value))} aria-label="سطح فعالیت">
                    {ACTIVITY_LEVELS.map((level) => <option key={level.value} value={level.value}>{level.label}</option>)}
                  </select>
                </label>
              </div>
            </div>
            <div className="tl-card tl-card--cyan">
              <NumField label="قد" unit="cm" value={height} min={100} max={230} step={1} onChange={setHeight} />
            </div>
            <div className="tl-card tl-card--mint">
              <NumField label="وزن" unit="kg" value={weight} min={30} max={200} step={1} onChange={setWeight} />
            </div>
            {validationMessage && <p className="tl-error" role="alert">{validationMessage}</p>}
            <button type="button" className="tl-btn tl-btn--accent tl-btn--lg" onClick={handleCalculate}>
              محاسبه و تحلیل
            </button>
          </section>
          <section className="tl-col">
            <div className="tl-card tl-card--paper tl-live" style={{ '--bucket': bmiTone(bmi) }}>
              <span className="tl-live__label">شاخص توده بدنی شما</span>
              <strong className="tl-live__value" dir="ltr">{bmi.toFixed(1)}</strong>
              <span className="tl-live__chip">{bucket.label}</span>
              <div className="tl-scale" dir="rtl" aria-hidden="true">
                <div className="tl-scale__bar">
                  {BMI_SCALE.map((z) => <i key={z.to} style={{ background: z.color, flex: z.w }} />)}
                  <b style={{ insetInlineStart: scalePos(bmi) + '%' }} />
                </div>
                <div className="tl-scale__ticks">
                  {SCALE_TICKS.map((t) => <span key={t.label} style={{ insetInlineStart: t.at + '%' }}>{t.label.toLocaleString('fa-IR')}</span>)}
                </div>
              </div>
              <div className="tl-live__meta">
                <span>قد <b>{height}</b> cm</span>
                <span>وزن <b>{weight}</b> kg</span>
              </div>
            </div>
            {!report && (
              <div className="tl-card tl-card--paper tl-empty">
                <p>سن رو وارد کن و «محاسبه و تحلیل» رو بزن تا BMR، TDEE، کالری هدف و راهنمای تغذیه نمایش داده بشه.</p>
              </div>
            )}
          </section>
        </div>
        {report && (
          <div ref={resultRef} className="tl-result">
            <BmiResultDock bmi={report.bmi} bucket={report.bucket} height={report.height} weight={report.weight} activityLabel={report.activityLabel} report={report} />
          </div>
        )}
      </div>
    </div>
  );
}
